// Client-side phone verification for the Extensión (aspirantes) public flow.
//
// Uses Firebase Phone Auth (RecaptchaVerifier + signInWithPhoneNumber). This is
// client-side by design: Google runs its anti-abuse (reCAPTCHA) in the browser
// and only issues a signed ID token afterward. The backend proves ownership by
// verifying that token's `phone_number` claim (see gfunctions/extension:
// aspiranteOtp.verifyPhoneToken and intakeAspirante's phone gate).
//
// We never keep the phone-only Firebase session: confirmPhoneCode captures the
// ID token and immediately signs out, so the app's own (staff) auth model is
// untouched. The resulting phone-only Auth users have no Firestore profile and
// are rejected by ProtectedRoute.

import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";

import { userAuth } from "../../config/ConfigFirebase";
import { DEFAULT_PHONE_PREFIX } from "../../constants";
import { debugLog } from "../../functions";

// Generic message for anything the citizen cannot act on. See
// phoneAuthErrorMessage for why every infrastructure fault collapses to this.
const GENERIC_PHONE_ERROR =
  "No fue posible enviar el código en este momento. Intenta más tarde o comunícate con nosotros.";

// Maps a Firebase Auth error to a citizen-facing Spanish message. Same shape as
// applicantErrorMessage (services/backend/ApplicantService) so both read alike.
//
// Deliberately two-tier: only errors the CITIZEN can act on get a specific
// message. Everything else — provider disabled, no Blaze billing, SMS quota
// exhausted, bad reCAPTCHA/API key, unauthorized domain — is an internal fault
// of ours on an unauthenticated public form, so it collapses to one generic
// message that reveals nothing about the project's configuration. The real code
// goes to debugLog (dev-only), never to the screen.
const PHONE_AUTH_MESSAGES = {
  // Tier A — actionable by the citizen.
  "auth/invalid-phone-number": "El número de celular no es válido. Verifica el indicativo del país y los 10 dígitos.",
  "auth/missing-phone-number": "Ingresa un número de celular.",
  "auth/too-many-requests": "Demasiados intentos. Espera unos minutos antes de solicitar otro código.",
  "auth/network-request-failed": "Problema de conexión. Verifica tu internet e intenta de nuevo.",
  "auth/code-expired": "El código expiró. Solicita uno nuevo.",
  "auth/invalid-verification-code": "Código incorrecto. Revísalo e intenta de nuevo.",
  // Tier B — our own configuration/infrastructure. Listed explicitly so it is
  // clear they are handled, but all resolve to the same opaque message.
  "auth/operation-not-allowed": GENERIC_PHONE_ERROR,
  "auth/billing-not-enabled": GENERIC_PHONE_ERROR,
  "auth/quota-exceeded": GENERIC_PHONE_ERROR,
  "auth/invalid-app-credential": GENERIC_PHONE_ERROR,
  "auth/captcha-check-failed": GENERIC_PHONE_ERROR,
  "auth/app-not-authorized": GENERIC_PHONE_ERROR,
  "auth/internal-error": GENERIC_PHONE_ERROR,
};

export function phoneAuthErrorMessage(err) {
  const code = (err && err.code) || "";
  // Dev-only: the one place the real cause is legible. Production builds print
  // nothing (debugLog is a no-op outside NODE_ENV === "development").
  //
  // The auth/* code alone is ambiguous (invalid-app-credential covers several
  // distinct causes) and the SDK discards Identity Toolkit's body for most
  // errors, so the raw reason lives in the Network tab, not here. The full error
  // object is logged so customData/stack are expandable in the console.
  debugLog("[PhoneVerification]", code, (err && err.message) || err, err);
  return PHONE_AUTH_MESSAGES[code] || GENERIC_PHONE_ERROR;
}

// Normalize a Colombian mobile to E.164 (+57XXXXXXXXXX). Numbers that already
// carry a `+` country code are kept as-is (digits only). Returns "" if empty.
export function toE164(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (s.startsWith("+")) return "+" + s.slice(1).replace(/\D/g, "");
  const digits = s.replace(/\D/g, "");
  if (!digits) return "";
  return `${DEFAULT_PHONE_PREFIX}${digits}`;
}

// Basic plausibility check on the normalized E.164 number: a leading + and 7–15
// digits. Generic across countries (the country selector picks the dial code);
// Firebase rejects any number that is truly invalid on send.
export function isValidPhone(raw) {
  return /^\+\d{7,15}$/.test(toE164(raw));
}

// Invisible reCAPTCHA verifier. grecaptcha keeps an internal per-DOM-node
// registry and refuses to render into a node it has already used — emptying the
// node's innerHTML does NOT clear that, so reusing one static container throws
// "reCAPTCHA has already been rendered in this element". We therefore build each
// verifier on a fresh throwaway child element and remove it on teardown, so
// grecaptcha never sees the same node twice.
let _verifier = null;
let _hostEl = null;

export function getRecaptcha(containerId) {
  clearRecaptcha();
  if (typeof document === "undefined") throw new Error("NO_DOM");
  const wrapper = document.getElementById(containerId);
  if (!wrapper) throw new Error("RECAPTCHA_CONTAINER_MISSING");
  _hostEl = document.createElement("div");
  wrapper.appendChild(_hostEl);
  _verifier = new RecaptchaVerifier(userAuth, _hostEl, { size: "invisible" });
  return _verifier;
}

export function clearRecaptcha() {
  if (_verifier) {
    try { _verifier.clear(); } catch (_) { /* already cleared */ }
  }
  if (_hostEl && _hostEl.parentNode) {
    try { _hostEl.parentNode.removeChild(_hostEl); } catch (_) { /* gone */ }
  }
  _verifier = null;
  _hostEl = null;
}

// Send a 6-digit SMS code to `rawPhone`. `containerId` is the id of the hidden
// div hosting the invisible reCAPTCHA. Returns the ConfirmationResult that
// confirmPhoneCode consumes, or throws on failure (reCAPTCHA / invalid number /
// quota). The reCAPTCHA is reset after each send so the next attempt is clean.
//
// `opts.testMode` (driven by config/aspirantes → smsTestMode) disables app
// verification so whitelisted test numbers resolve without a real SMS/reCAPTCHA.
// ⚠️ With it on, REAL numbers fail (auth/invalid-app-credential) — reCAPTCHA is
// off, so no valid app credential is sent. We set the flag explicitly (both
// true AND false) so toggling test mode off restores the normal flow.
export async function sendPhoneCode(rawPhone, containerId, opts = {}) {
  const e164 = toE164(rawPhone);
  if (!isValidPhone(e164)) throw new Error("INVALID_PHONE");
  userAuth.settings.appVerificationDisabledForTesting = !!opts.testMode;
  const verifier = getRecaptcha(containerId);
  try {
    return await signInWithPhoneNumber(userAuth, e164, verifier);
  } catch (err) {
    // The real cause is logged (dev-only) by phoneAuthErrorMessage, which every
    // caller runs the error through. Nothing is printed in production builds.
    clearRecaptcha(); // force a fresh challenge next attempt
    throw err;
  }
}

// Confirm the SMS code against a ConfirmationResult. On success captures the
// Firebase ID token (proof of phone ownership for the backend) and the verified
// phone number, then signs the throwaway phone session out. Returns
// { idToken, phoneNumber }.
export async function confirmPhoneCode(confirmationResult, code) {
  const cred = await confirmationResult.confirm(String(code).trim());
  const user = cred.user;
  const idToken = await user.getIdToken();
  const phoneNumber = user.phoneNumber || null;
  try { await signOut(userAuth); } catch (_) { /* best effort */ }
  return { idToken, phoneNumber };
}
